import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, Row } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router';
import MDEditor from '@uiw/react-md-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import Contents from 'components/Layout/Contents';
import PartnerService from 'api/PartnerService';
import Spinner from 'components/Spinner/Spinner';
import './PartnerProfile.scss';
import useLayout from 'components/Layout/useLayout';
import ContentBlockList from './ContentBlockList';

export default function PartnerProfile() {
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const location = useLocation();
  const [partnerInformation, setPartnerInformation] = useState([]);
  const [processing, setProcessing] = useState(false);
  const { scrollToTop } = useLayout();

  const { isLoading, refetch } = useQuery(
    ['partners', partnerId],
    () => {
      setProcessing(true);
      return PartnerService.getOneById(partnerId);
    },
    {
      enabled: false,
      onSuccess: (data) => {
        if (data.data) {
          const response = data.data;
          const socialMedia = {};
          response?.socialMedia?.forEach((sm) => {
            socialMedia[sm.network] = {
              network: sm.isVisible ? sm.network : false,
              location:
                sm.location !== null && sm.location.toString() !== 'null'
                  ? sm.location
                  : '',
            };
          });
          response.socialMedia = socialMedia;
          setPartnerInformation(response);
        }
        setProcessing(false);
      },
    }
  );

  const partnerProfileButtons = [
    {
      label: 'Back to List',
      click: () => {
        // eslint-disable-next-line no-alert
        navigate(`/partners`, {
          state: { search: location.state?.search || '' },
        });
      },
    },
    {
      label: 'Edit Partner Information',
      click: () => {
        // eslint-disable-next-line no-alert
        navigate(`/partners/${partnerId}/edit`);
      },
    },
  ];

  const statusColor = () => {
    if (partnerInformation) {
      if (partnerInformation?.status?.toLowerCase() === 'active') {
        return 'success';
      }
      if (partnerInformation?.status === 'Draft') {
        return 'info';
      }
      if (partnerInformation?.status === 'Registered') {
        return 'warning';
      }
    }
    return 'secondary';
  };
  useEffect(() => {
    if (partnerId) {
      refetch();
    }
    scrollToTop();
  }, []);

  if (isLoading || processing) {
    return <Spinner />;
  }

  const formatAddress = (address) => {
    if (address) {
      const fields = [
        [address.suite],
        [address.street],
        [address.city],
        [address.state],
        [address.country],
        [address.zipCode],
      ];
      const formattedAddress = fields
        .map((part) => part.filter(Boolean).join(' '))
        .filter((str) => str.length)
        .join(', ');

      return formattedAddress;
    }
    return null;
  };

  return (
    <Contents>
      <div className="partner-profile">
        <div className="d-flex mb-3">
          <h2 className="page-content-header mb-0">Partner Information</h2>
          {partnerProfileButtons?.length > 0 && (
            <div className="flex-grow-1 d-flex align-content-center align-items-center justify-content-end  text-end">
              <ButtonGroup size="sm">
                {partnerProfileButtons &&
                  partnerProfileButtons.map((b, index) => (
                    <Button
                      variant="outline-primary"
                      key={`pi-btn-group-item-${index.toString()}`}
                      onClick={b.click}
                    >
                      {b.label}
                    </Button>
                  ))}
              </ButtonGroup>
            </div>
          )}
        </div>
        <hr />
        <div className="d-flex mb-3">
          <div className="d-block pe-3 pe-lg-3 pe-md-3 pe-sm-0">
            <div
              style={{ width: '250px' }}
              className="d-flex align-content-center align-items-center"
            >
              <span className="d-block me-2">Tier</span>
              <span className="d-block form-control" data-testid="Value-tier">
                {partnerInformation?.tierTitle}
              </span>
            </div>
          </div>
          <div className="d-block flex-grow-1">
            <div
              style={{ width: '250px' }}
              className="d-flex align-content-center align-items-center"
            >
              <span className="d-block me-2">Template</span>
              <span
                className="d-block form-control"
                data-testid="Value-template"
              >
                {partnerInformation?.templateId}
              </span>
            </div>
          </div>
          <div className="d-block">
            <Button
              variant={statusColor()}
              disabled
              className="text-white text-capitalize"
              style={{ cursor: 'default', opacity: 1, userSelect: 'none' }}
              data-testid="Button-status"
            >
              {partnerInformation?.status}
            </Button>
          </div>
        </div>
        <div className="mb-3 d-block float-none">
          <div className="partner-logo text-end float-xl-end float-lg-end float-md-end float-sm-none position-relative shadow-sm">
            <img
              src={
                partnerInformation?.logo && partnerInformation?.logo?.length > 0
                  ? partnerInformation.logo
                  : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
              }
              alt={partnerInformation?.title}
            />
            {!partnerInformation.logo && (
              <div className="d-flex h-100 align-items-center align-content-center justify-content-center position-absolute top-0 start-0 end-0 bottom-0 opacity-25">
                <FontAwesomeIcon icon={faImage} size="3x" />
              </div>
            )}
          </div>
          <div className="partner-information float-start">
            <Row>
              <Col>
                <ul className="list-unstyled   p-3">
                  <li className="mb-2">
                    <small className="info-label text-muted">ID</small>
                    <div className="info-value" data-testid="Value-id">
                      {partnerInformation?.id}
                    </div>
                  </li>
                  <li className="mb-2">
                    <small className="info-label text-muted">Title</small>
                    <div className="info-value" data-testid="Value-title">
                      {partnerInformation?.title}
                    </div>
                  </li>
                  <li className="mb-2">
                    <small className="info-label text-muted">Name</small>
                    <div className="info-value" data-testid="Value-name">
                      {partnerInformation?.name}
                    </div>
                  </li>
                  <li className="mb-2">
                    <small className="info-label text-muted">Address</small>
                    <div className="info-value" data-testid="Value-address">
                      {formatAddress(partnerInformation?.address)}
                    </div>
                  </li>
                </ul>
              </Col>
              <Col>
                <ul className="list-unstyled p-3">
                  {partnerInformation?.socialMedia?.facebook?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">Facebook</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-facebook"
                      >
                        {partnerInformation?.socialMedia?.facebook?.location}
                      </div>
                    </li>
                  )}
                  {partnerInformation?.socialMedia?.twitter?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">Twitter</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-twitter"
                      >
                        {partnerInformation?.socialMedia?.twitter?.location}
                      </div>
                    </li>
                  )}
                  {partnerInformation?.socialMedia?.instagram?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">Instagram</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-instagram"
                      >
                        {partnerInformation?.socialMedia?.instagram?.location}
                      </div>
                    </li>
                  )}
                  {partnerInformation?.socialMedia?.youtube?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">Youtube</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-youtube"
                      >
                        {partnerInformation?.socialMedia?.youtube?.location}
                      </div>
                    </li>
                  )}
                  {partnerInformation?.socialMedia?.linkedin?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">LinkedIn</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-linkedin"
                      >
                        {partnerInformation?.socialMedia?.linkedin?.location}
                      </div>
                    </li>
                  )}
                  {partnerInformation?.socialMedia?.vimeo?.network && (
                    <li className="mb-2">
                      <small className="info-label text-muted">Vimeo</small>
                      <div
                        className="info-value"
                        data-testid="Value-social-vimeo"
                      >
                        {partnerInformation?.socialMedia?.vimeo?.location}
                      </div>
                    </li>
                  )}
                </ul>
              </Col>
            </Row>
          </div>
          <span className="d-block clearfix float-none" />
        </div>
        <div className="partner-description float-none mb-3">
          <strong className="d-block">Description</strong>
          <div className="description-content p-3" data-color-mode="light">
            <MDEditor.Markdown source={partnerInformation?.description} />
          </div>
        </div>
        {partnerInformation.specialtiesData &&
          partnerInformation.specialtiesData.length > 0 && (
            <div className="specialties-block float-none">
              <strong className="d-block">Specialties</strong>
              <div className="specialties-content p-3" data-color-mode="light">
                <ul className="list-unstyled d-flex mb-0">
                  {partnerInformation.specialtiesData.map((sp) => (
                    <li className="d-block mx-2" key={sp.id}>
                      <span
                        className="btn btn-sm btn-outline-light text-dark"
                        data-testid="Value-competency"
                      >
                        {sp.name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
      </div>
      <ContentBlockList
        partnerId={partnerId}
        allowedBlocks={partnerInformation?.allowedBlocks}
      />
    </Contents>
  );
}
